import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../utils/date';
import { API_BASE } from '../config';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface ReportedScam {
  id: number;
  url: string;
  firstSeenAt: string;
  scamType?: string;
  description?: string;
}

export function ScamList() {
  const { t } = useTranslation();
  const [scams, setScams] = useState<ReportedScam[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScams();
  }, []);

  const loadScams = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/v1/scams?limit=50`);
      const data = await res.json();
      setScams(data);
    } catch (error) {
      console.error('Failed to load scams:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="content-wrapper--wide">
        <div className="mb-4 flex items-center justify-end">
          <LanguageSwitcher />
        </div>
        <h1 className="page-title">{t('scams.title')}</h1>
        <p className="text-gray-600 mb-6">{t('scams.description')}</p>

        {loading ? (
          <div className="text-center py-8 text-gray-500">{t('common.loading')}</div>
        ) : (
          <div className="card-compact divide-y">
            {scams.map((scam) => (
              <Link
                key={scam.id}
                to={`/scam/${scam.id}`}
                className="clickable-row block p-4"
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="shrink-0">
                    <div className="logo-thumb logo-placeholder rounded" aria-hidden="true">
                      <span className="sr-only">{t('alt.logoPlaceholder')}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-red-600 font-medium mb-1">
                      {scam.scamType || t('scams.unknownType')}
                    </div>
                    <div className="text-blue-600 font-mono text-sm break-all mb-2">
                      {scam.url}
                    </div>
                    {scam.description && (
                      <p className="text-gray-700 text-sm line-clamp-2">
                        {scam.description}
                      </p>
                    )}
                    <div className="text-xs text-gray-500 mt-2">
                      {t('scams.firstSeenAt')}: {formatDateTime(scam.firstSeenAt)}
                    </div>
                  </div>
                  <span className="text-gray-400 ml-4">→</span>
                </div>
              </Link>
            ))}
            {scams.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                {t('scams.noReports')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
